import axios from "axios";
import { API_BASE_URL } from "configs/AppConfig";
import { signOutSuccess } from "store/slices/authSlice";
import store from "../store";
import { notification } from "antd";
import {
  ERROR_CODES,
  ERROR_MESSAGES,
  TOKEN_PAYLOAD_KEY,
  AUTH_TOKEN,
} from "constants/ErrorConstants";
import { USER_DATA } from "constants/AuthConstant";

const unauthorizedCode = [ERROR_CODES.UNAUTHORIZED, ERROR_CODES.FORBIDDEN];

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

const getClientTimezone = ()=>{
  const offset = -new Date().getTimezoneOffset(); // in minutes
const sign = offset >= 0 ? '+' : '-';
const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
const minutes = String(Math.abs(offset) % 60).padStart(2, '0');
const gmtOffset = `GMT${sign}${hours}${minutes}`;
return gmtOffset
}

let notificationTimer = null;
const notificationCooldown = 5000;

const showNotificationWithCooldown = (message, description) => {
  if (!notificationTimer) {
    notification.error({
      message,
      description,
    });

    notificationTimer = setTimeout(() => {
      notificationTimer = null;
    }, notificationCooldown);
  }
};
// Maintain a list of active controllers
const activeControllers = new Set();

service.interceptors.request.use(
  (config) => {
    const controller = new AbortController();
    config.signal = controller.signal;
    controller.metadata = { url: config.url };
    activeControllers.add(controller);
    config.metadata = { controller, url: config.url };
    config.method = config.method.toLowerCase();
    const jwtToken = localStorage.getItem(AUTH_TOKEN) || null;
    if (jwtToken) {
      config.headers[TOKEN_PAYLOAD_KEY] = jwtToken;
    }
    const timezone = getClientTimezone();
    if (config.params instanceof URLSearchParams) {
      config.params.append('timezone', timezone);
    } else {
      const existingParams = config.params || {};
      config.params = {
        ...existingParams,
        timezone,
      };
    }
    // const existingParams =
    // config.params instanceof URLSearchParams
    // ? Object.fromEntries(config.params.entries())
    // : config.params || {};

    // config.params = {
    //   ...existingParams,
    //   timezone,
    // };
    const trimStrings = (data) => {
      if (Array.isArray(data)) {
        return data.map(trimStrings);
      } else if (typeof data === "object" && data !== null) {
        return Object.keys(data).reduce((acc, key) => {
          acc[key] = trimStrings(data[key]); 
          return acc;
        }, {});
      } else if (typeof data === "string") {
        return data.trim();
      }
      return data;
    };

     const removeCurrencySymbol = (data) => {
        if (Array.isArray(data)) {
          return data.map(removeCurrencySymbol);
        } else if (typeof data === "object" && data !== null) {
          return Object.entries(data).reduce((acc, [key, value]) => {
            acc[key] = removeCurrencySymbol(value);
            return acc;
          }, {});
        } else if (typeof data === "string" && data.includes("₹")) {
          return parseFloat(data.replace(/₹/g, '').trim()) || 0;
        }
        return data;
      };

    if (config.data && typeof config.data === "object" && !(config.data instanceof FormData)) {
      config.data = trimStrings(config.data);
      config.data = removeCurrencySymbol(config.data); 
    }

    if (
      config.url.includes("send_otp") ||
      config.url.includes("login_user") ||
      config.url.includes("verify_otp") ||
      config.url.includes("forgot_password")
    ) {
      return config;
    } else {
      const role = localStorage.getItem(USER_DATA);

      if (!role) {
        localStorage.clear();
        store.dispatch(signOutSuccess());

        showNotificationWithCooldown(
          ERROR_MESSAGES.AUTHENTICATION_FAILED,
          ERROR_MESSAGES.LOGIN_AGAIN
        );
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

service.interceptors.response.use(
  (response) => {
     if (response.config?.metadata?.controller) {
      activeControllers.delete(response.config.metadata.controller);
    }
    return response.data;
  },

  async (error) => {
    if (error.config?.metadata?.controller) {
      activeControllers.delete(error.config.metadata.controller);
    }
    const { config, response } = error;

    if (!response) {
      showNotificationWithCooldown(
        ERROR_MESSAGES.NO_RESPONSE,
        ERROR_MESSAGES.CHECK_NETWORK
      );
      return Promise.reject(error);
    }
    const { status, data } = response;
    if (config.method === "get") {
      if (!config.__retryCount) {
        config.__retryCount = 0;
      }

      const maxRetries = 1;
      if (config.__retryCount < maxRetries) {
        config.__retryCount += 1;

        await new Promise((resolve) => setTimeout(resolve, 5000));
        return service(config);
      }
    }
    let notificationParam = { message: "", description: "" };
    if (unauthorizedCode.includes(status)) {
      notificationParam.message = ERROR_MESSAGES.AUTHENTICATION_FAILED;
      notificationParam.description = ERROR_MESSAGES.LOGIN_AGAIN;
      localStorage.clear();
      store.dispatch(signOutSuccess());
    } else if (status === ERROR_CODES.NOT_FOUND) {
      notificationParam.message = ERROR_MESSAGES.NOT_FOUND;
      notificationParam.description = ERROR_MESSAGES.RESOURCE_NOT_FOUND;
    } else if (status === ERROR_CODES.INTERNAL_SERVER_ERROR) {
      notificationParam.message = ERROR_MESSAGES.SERVER_ERROR;
      notificationParam.description = ERROR_MESSAGES.SERVER_ISSUE;
    } else if (status === ERROR_CODES.TIMEOUT) {
      notificationParam.message = ERROR_MESSAGES.TIMEOUT;
      notificationParam.description = ERROR_MESSAGES.REQUEST_TIMEOUT;
    } else {
      // if(data?.error == "Invalid credentials"){
      if((data?.error && data?.error!="") || (data?.data?.message && data?.data?.message!="")){
        notificationParam.message = ERROR_MESSAGES.GENERIC_ERROR;
        // notificationParam.description = ERROR_MESSAGES.INVALID_CREDENTIAL;
        notificationParam.description = data?.error ? data?.error : data?.data?.message
      } else{
        notificationParam.message = ERROR_MESSAGES.GENERIC_ERROR;
        notificationParam.description = ERROR_MESSAGES.UNEXPECTED_ERROR;
      }
    }

    showNotificationWithCooldown(
      notificationParam.message,
      notificationParam.description
    );
    return Promise.reject(error);
  }
);

export const cancelAllRequests = (message = 'Operation canceled by user') => {
  // console.log(`Cancelling ${activeControllers.size} active requests`);
  
  // Abort all controllers
  activeControllers.forEach(controller => {
    console.log("Aborting:", controller?.metadata?.url);

    try {
      controller.abort(message);
    } catch (e) {
      console.error('Error aborting request:', e);
    }
  });
  
  // Clear the map
  activeControllers.clear();
  return true;
};
export default service;


